import { ApiError } from "@chai/api-client";
import type { Me } from "@chai/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useApi } from "../../api";
import { useAuth } from "../../auth";
import { useDocumentTitle } from "@/lib/document-title";

const SearchSchema = z.object({
  returnTo: z.string().optional(),
});

export const Route = createFileRoute("/_auth/signin")({
  validateSearch: (search) => SearchSchema.parse(search),
  component: SignInPage,
});

/** Allowlist for `returnTo` redirects — same origin only (admin is now the
 * in-app /manage section, so there's no cross-origin target). Anything else
 * falls back to the in-app workspaces page.
 */
function safeReturnTo(raw: string | undefined): { kind: "internal"; path: string } | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return { kind: "internal", path: url.pathname + url.search + url.hash };
  } catch {
    return null;
  }
}

function SignInPage() {
  const api = useApi();
  const auth = useAuth();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { returnTo } = useSearch({ from: "/_auth/signin" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // A demo tenant's sign-in page introduces itself: what this is, whose demo,
  // and what the visitor is about to get — the four authored context lines
  // served anonymously by host (GET /api/demo/entry-brief). A 404 or any
  // failure means "not a demo host" and the page reads exactly as before.
  const [demoBrief, setDemoBrief] = useState<{
    client_name: string;
    product_line: string;
    expect_line: string;
  } | null>(null);
  useEffect(() => {
    let alive = true;
    void fetch("/api/demo/entry-brief")
      .then((res) => (res.ok ? res.json() : null))
      .then((body: unknown) => {
        if (!alive || typeof body !== "object" || body === null) return;
        const b = body as Record<string, unknown>;
        const pick = (k: string) => (typeof b[k] === "string" ? (b[k] as string) : "");
        const brief = {
          client_name: pick("client_name"),
          product_line: pick("product_line"),
          expect_line: pick("expect_line"),
        };
        if (brief.client_name || brief.product_line) setDemoBrief(brief);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  // Arrived from a shared page's "Sign in" (`/s/{slug}`): say so, instead of
  // promising workspaces to someone who was sent one page.
  const forSharedPage = (safeReturnTo(returnTo)?.path ?? "").startsWith("/s/");

  /** Apply the post-login redirect rules: returnTo allowlist → an
   * artifact-only account's shared pages → the demo user's landing page
   * (per-seat/per-demo, from /me's demo block) → the in-app workspaces
   * fallback. */
  const goToDestination = useCallback(() => {
    const target = safeReturnTo(returnTo);
    if (target?.kind === "internal") {
      window.location.href = target.path;
      return;
    }
    // Read the FRESH me from the query cache: right after `await
    // auth.refresh()` the cache holds the signed-in user, while this
    // closure's `auth.me` can still be the pre-login value (React hasn't
    // re-rendered yet).
    const me = queryClient.getQueryData<Me>(["me"]) ?? auth.me;
    // An account that opens shared pages and nothing else: straight to its
    // page when it has one, else the list (`/shared` decides).
    if (me?.user.artifact_only) {
      window.location.href = "/shared?auto=1";
      return;
    }
    // Demo prospects may carry a landing page.
    const landing = me?.demo?.landing_path?.trim() ?? "";
    if (landing.startsWith("/") && !landing.startsWith("//")) {
      // history.push (not the typed navigate()): the landing path is a
      // runtime string from the demo spec, unknowable to the route typegen
      // (same escape the demo tour uses). A path that 404s or trips a guard
      // is handled by the app's normal routing — no special fallback here.
      router.history.push(landing);
      return;
    }
    void navigate({ to: "/workspaces" });
  }, [returnTo, navigate, router, queryClient, auth.me]);

  /** If the user is already signed in when they land on /signin (e.g. opened
   * the admin app, got bounced here, but their .chaidojo.com session cookie
   * was still valid), don't make them re-enter credentials — send them on.
   * `switchingAccount` lets them opt out of the auto-redirect to sign in as
   * someone else from this same page.
   */
  useEffect(() => {
    if (auth.isLoading) return;
    if (!auth.me) return;
    if (switchingAccount) return;
    goToDestination();
  }, [auth.isLoading, auth.me, switchingAccount, goToDestination]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await api.login(email, password);
      await auth.refresh();
      goToDestination();
      return;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 400 || err.status === 401) {
          setError("That email and password don't match. Check both and try again.");
        } else if (err.status === 403) {
          setError("This account isn't allowed to sign in.");
        } else if (err.status >= 500) {
          setError("Something went wrong on our end. Please try again in a moment.");
        } else {
          setError(err.message || "Sign-in failed.");
        }
      } else {
        setError((err as Error).message || "Sign-in failed.");
      }
    } finally {
      setPending(false);
    }
  };

  // ── States rendered before the form ────────────────────────────────────────

  // Auth state still loading: avoid form flicker on a freshly-bounced visit.
  useDocumentTitle(["Sign in", demoBrief?.client_name ? `${demoBrief.client_name} demo` : null]);

  if (auth.isLoading) {
    return (
      <div className="flex flex-col gap-4 pt-16 text-center">
        <Spinner className="mx-auto h-5 w-5" />
        <p className="text-sm text-muted-foreground">Checking your session…</p>
      </div>
    );
  }

  // Already signed in and not opted into account-switching → brief
  // "redirecting" UI while the useEffect above fires.
  if (auth.me && !switchingAccount) {
    return (
      <div className="flex flex-col gap-6 pt-10">
        <header>
          <p className="meta-strip mb-2">Welcome back</p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            You're already signed in
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Continuing as{" "}
            <span className="font-medium text-foreground">
              {auth.me.user.email}
            </span>
            …
          </p>
        </header>
        <Card>
          <CardContent className="flex flex-col gap-3 pt-6">
            <Button onClick={goToDestination} className="w-full">
              <Spinner data-icon="inline-start" />
              Continue
            </Button>
            <Button
              variant="ghost"
              onClick={async () => {
                await auth.logout();
                setSwitchingAccount(true);
              }}
              className="w-full"
            >
              Sign in as a different account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-10">
      <header>
        <p className="meta-strip mb-2">{demoBrief ? "Your demo" : "Welcome back"}</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          {demoBrief?.client_name
            ? `${demoBrief.client_name} — a Chaidojo demo`
            : forSharedPage
              ? "Sign in to open this page"
              : "Sign in to Chaidojo"}
        </h1>
        {demoBrief ? (
          <div className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground">
            {demoBrief.product_line ? <p>{demoBrief.product_line}</p> : null}
            {demoBrief.expect_line ? <p>{demoBrief.expect_line}</p> : null}
          </div>
        ) : forSharedPage ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Use the email address the page was shared with.
          </p>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            Pick up where you left off — your workspaces and canvases are waiting.
          </p>
        )}
      </header>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} noValidate>
            <FieldGroup>
              <Field data-invalid={error ? "" : undefined}>
                <FieldLabel htmlFor="signin-email">Email</FieldLabel>
                <Input
                  id="signin-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!error || undefined}
                />
              </Field>
              <Field data-invalid={error ? "" : undefined}>
                <FieldLabel htmlFor="signin-password">Password</FieldLabel>
                <div className="relative">
                  <Input
                    id="signin-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={!!error || undefined}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-r-md"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
                <FieldDescription>
                  {/* A demo prospect never set a password — they were handed
                      one. Telling them to "use the password you set" sends
                      them hunting for a reset flow that doesn't exist. */}
                  {demoBrief
                    ? "Use the email and password you were given for this demo."
                    : "Use the password you set when you joined."}
                </FieldDescription>
              </Field>

              {error && (
                <Alert variant="destructive" role="alert" aria-live="polite">
                  <AlertTitle>Sign-in failed</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={pending} className="w-full">
                {pending && <Spinner data-icon="inline-start" />}
                Sign in
              </Button>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>

      {/* Never on a demo host: a prospect handed a seat must not be offered an
          org-creation escape hatch ("spin up an org", "invite token" — product
          jargon on the one page that introduces the demo in their language),
          and following it would walk them out of the room they were invited
          into. The SERVER is now the signal: /auth/config returns
          allowSignup:false on any demo hostname (and /auth/signup refuses a
          POST there), so this card cannot render on a demo even for a demo
          that carries no brief — which was every demo in production. The
          !demoBrief clause is kept as belt-and-braces. */}
      {/* Nor for someone sent here by a shared page: they were given an
          account, and an org-signup card is a door they should not take. */}
      {auth.authConfig?.allowSignup && !demoBrief && !forSharedPage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Don't have an account?</CardTitle>
            <CardDescription>Spin up an org or join one with an invite token.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Link
              to="/signup"
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              Create an account →
            </Link>
          </CardFooter>
        </Card>
      )}
    </div>
  );
}
